import { createStore } from "solid-js/store";
import { IcyMetadata } from "~/types/metadata";
import {
  PlayerChannel,
  PlaybackMode,
  PodcastEpisodePlayback,
  PodcastPageContext,
} from "~/types/player";
import { logger } from "~/lib/logger/logger";

interface PlayerState {
  autoPlay: boolean;
  currentPlaylist: PlayerChannel[] | null;
  currentStream: PlayerChannel | null;
  isLoading: boolean;
  isMobileChannelSwitcherExpanded: boolean;
  isPlayerExpanded: boolean;
  isPlaying: boolean;
  isVolumeOpen: boolean;
  playerColour: string;
  streamMetadata: IcyMetadata | null;

  // Podcast playback state
  playbackMode: PlaybackMode;
  podcastEpisode: PodcastEpisodePlayback | null;
  podcastCurrentTime: number;

  // Podcast page context - set by podcast pages, used by audio blocks to get metadata
  podcastPageContext: PodcastPageContext | null;
}

const initialState: PlayerState = {
  autoPlay: false,
  currentPlaylist: null,
  currentStream: null,
  isLoading: false,
  isMobileChannelSwitcherExpanded: false,
  isPlayerExpanded: true,
  isPlaying: false,
  isVolumeOpen: false,
  playerColour: "var(--bg-player)",
  streamMetadata: null,

  // Podcast playback - default to stream mode
  playbackMode: "stream",
  podcastEpisode: null,
  podcastCurrentTime: 0,

  // Podcast page context - null when not on a podcast page
  podcastPageContext: null,
};

export const [audioPlayerStore, setAudioPlayerStore] = createStore<PlayerState>(initialState);

// Actions
export const audioPlayerActions = {
  setAutoPlay: (autoPlay: boolean) => setAudioPlayerStore("autoPlay", autoPlay),
  setCurrentPlaylist: (playlist: PlayerChannel[] | null) =>
    setAudioPlayerStore("currentPlaylist", playlist),
  setCurrentStream: (stream: PlayerChannel | null) => {
    // Create a shallow copy to avoid sharing references with playlist items
    // This prevents SolidJS reactivity from mutating the playlist
    const streamCopy = stream ? { ...stream } : null;
    setAudioPlayerStore("currentStream", streamCopy);
  },
  setIsLoading: (isLoading: boolean) => setAudioPlayerStore("isLoading", isLoading),
  setIsMobileChannelSwitcherExpanded: (isExpanded: boolean) =>
    setAudioPlayerStore("isMobileChannelSwitcherExpanded", isExpanded),
  setIsPlayerExpanded: (isPlayerExpanded: boolean) =>
    setAudioPlayerStore("isPlayerExpanded", isPlayerExpanded),
  setIsPlaying: (isPlaying: boolean) => setAudioPlayerStore("isPlaying", isPlaying),
  setIsVolumeOpen: (isVolumeOpen: boolean) => setAudioPlayerStore("isVolumeOpen", isVolumeOpen),
  setPlayerColour: (colour: string) => setAudioPlayerStore("playerColour", colour),
  setStreamMetadata: (metadata: IcyMetadata | null) =>
    setAudioPlayerStore("streamMetadata", metadata),

  // Podcast playback actions
  setPlaybackMode: (mode: PlaybackMode) => {
    logger.audio("setPlaybackMode:", mode);
    setAudioPlayerStore("playbackMode", mode);
  },
  setPodcastEpisode: (episode: PodcastEpisodePlayback | null) => {
    logger.audio("setPodcastEpisode:", episode?.title ?? "null");
    setAudioPlayerStore("podcastEpisode", episode);
  },
  setPodcastCurrentTime: (time: number) => setAudioPlayerStore("podcastCurrentTime", time),
  clearPodcast: () => {
    setAudioPlayerStore("podcastEpisode", null);
    setAudioPlayerStore("podcastCurrentTime", 0);
    setAudioPlayerStore("playbackMode", "stream");
  },

  // Podcast page context actions
  setPodcastPageContext: (context: PodcastPageContext | null) => {
    setAudioPlayerStore("podcastPageContext", context);
  },
  clearPodcastPageContext: () => {
    setAudioPlayerStore("podcastPageContext", null);
  },
};
